import React from "react";
import styles from "../../../../styles/AdminConsole.module.css";
import { MILESTONES } from "../../../../data/constants";
import SelectWrapper from "../../shared/components/SelectWrapper";
import Toggle from "../../shared/components/Toggle";
import DocChips from "../../shared/components/DocChips";

/**
 * VisitFormFields
 *
 * The inner form body shared by AddVisitModal and EditVisitModal.
 * Keeping this in one place means any future field addition happens once.
 *
 * Props:
 *   form          { milestone, date, description, concern, cleared, paidINR, documents }
 *   setForm       (updater) => void
 *   docInput      string
 *   setDocInput   (val) => void
 *   uploading     bool
 *   onFileUpload  (e: ChangeEvent) => void
 *   saving        bool
 *   submitLabel   string  — button text
 *   onSubmit      () => void
 */
const VisitFormFields = React.memo(function VisitFormFields({
  form,
  setForm,
  docInput,
  setDocInput,
  uploading,
  onFileUpload,
  saving,
  submitLabel,
  onSubmit,
}) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addDoc = () => {
    const v = docInput.trim();
    if (!v || form.documents.includes(v)) return;
    set("documents", [...form.documents, v]);
    setDocInput("");
  };
  const removeDoc = (item) =>
    set(
      "documents",
      form.documents.filter((x) => x !== item),
    );

  return (
    <>
      <div>
        <span
          className={styles.toggleLabelText}
          style={{ display: "block", marginBottom: 4, fontSize: 10 }}
        >
          Milestone *
        </span>
        <SelectWrapper
          value={form.milestone}
          onChange={(e) => set("milestone", e.target.value)}
        >
          {MILESTONES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </SelectWrapper>
      </div>

      <div>
        <span
          className={styles.toggleLabelText}
          style={{ display: "block", marginBottom: 4, fontSize: 10 }}
        >
          Visit Date *
        </span>
        <input
          className={styles.input}
          type="date"
          value={form.date}
          onChange={(e) => set("date", e.target.value)}
        />
      </div>

      <textarea
        className={styles.input}
        placeholder="Description"
        rows={3}
        style={{ resize: "vertical", lineHeight: 1.5 }}
        value={form.description}
        onChange={(e) => set("description", e.target.value)}
      />

      <textarea
        className={styles.input}
        placeholder="Concern (leave blank if none)"
        rows={2}
        style={{ resize: "vertical", lineHeight: 1.5 }}
        value={form.concern}
        onChange={(e) => set("concern", e.target.value)}
      />

      <div>
        <span
          className={styles.toggleLabelText}
          style={{ display: "block", marginBottom: 4, fontSize: 10 }}
        >
          Paid for this Visit (₹)
        </span>
        <input
          className={`${styles.input} ${styles.inputNumber}`}
          type="number"
          min="0"
          step="1"
          placeholder="0"
          value={form.paidINR}
          onChange={(e) => set("paidINR", e.target.value)}
        />
      </div>

      <Toggle
        label="Cleared"
        checked={!!form.cleared}
        onChange={() => set("cleared", !form.cleared)}
      />

      <div>
        <span
          className={styles.toggleLabelText}
          style={{ display: "block", marginBottom: 6, fontSize: 10 }}
        >
          Documents
        </span>
        <DocChips
          items={form.documents}
          inputValue={docInput}
          onInputChange={setDocInput}
          onAdd={addDoc}
          onRemove={removeDoc}
          onFileUpload={onFileUpload}
          uploading={uploading}
        />
      </div>

      <button
        className={`${styles.btn} ${styles.btnPrimary}`}
        disabled={saving || uploading}
        onClick={onSubmit}
      >
        {saving ? "Saving…" : submitLabel}
      </button>
    </>
  );
});

export default VisitFormFields;
